// Number helpers. How many decimal places a currency has comes from the
// browser's own ISO 4217 data, not from a table kept here.

export function digitsFor(currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency })
      .resolvedOptions().maximumFractionDigits;
  } catch {
    return 2;
  }
}

export function plain(minor, d) {
  return (minor / 10 ** d).toFixed(d);
}

export function money(minor, currency) {
  const d = digitsFor(currency);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(minor / 10 ** d);
  } catch {
    return `${plain(minor, d)} ${currency || ""}`.trim();
  }
}

const rand = (n) => Math.floor(Math.random() * n);

/** A basic sum whose answer is exactly `minor` (in minor units). */
export function makeProblem(minor, d) {
  const neg = minor < 0;
  const M = Math.abs(minor);
  const f = (n) => plain(n, d);
  let text;
  if (M < 2) {
    text = `${f(M)} + ${f(0)}`;
  } else {
    const kinds = ["add", "sub"];
    const factors = [];
    for (let k = 2; k <= 12; k++) if (M % k === 0) factors.push(k);
    if (factors.length) kinds.push("mul");
    const kind = kinds[rand(kinds.length)];
    if (kind === "add") {
      const a = 1 + rand(M - 1);
      text = `${f(a)} + ${f(M - a)}`;
    } else if (kind === "sub") {
      const b = 1 + rand(Math.max(M, 10));
      text = `${f(M + b)} − ${f(b)}`;
    } else {
      const k = factors[rand(factors.length)];
      text = `${f(M / k)} × ${k}`;
    }
  }
  return neg ? `−(${text})` : text;
}

export function shuffle(list) {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = rand(i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Three choices: the payment the sum equals, plus other payments' amounts in
 * the same currency, topped up with made-up decoys when there are not enough.
 */
export function makeChoices(target, pool, amountOf, currencyOf) {
  const M = amountOf(target);
  const cur = currencyOf(target);
  const d = digitsFor(cur);
  const seen = new Set([M]);
  const out = [{ minor: M, currency: cur, payment: target, correct: true }];
  for (const p of shuffle(pool)) {
    if (out.length >= 3) break;
    const m = amountOf(p);
    if (currencyOf(p) !== cur || seen.has(m)) continue;
    seen.add(m);
    out.push({ minor: m, currency: cur, payment: p, correct: false });
  }
  let guard = 0;
  while (out.length < 3 && guard++ < 50) {
    const step = 10 ** d * (1 + rand(9));
    const m = Math.random() < 0.5 ? M + step : Math.max(0, M - step);
    if (seen.has(m)) continue;
    seen.add(m);
    out.push({ minor: m, currency: cur, payment: null, correct: false });
  }
  return shuffle(out);
}
